import React, { useEffect, useState } from 'react';
import {
  MessageSquare, Bot, Megaphone, BadgeCheck, Users, 
  Database, BarChart, ArrowRight, CheckCircle2, 
  Smartphone, Zap, ShieldCheck, Send
} from 'lucide-react';
import SEO from './SEO';

const Whatsappapi = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setIsVisible(true);
  }, []);

  return (
    <div className="bg-[#050810] min-h-screen text-white overflow-hidden selection:bg-[#25D366]/30" style={{ fontFamily: 'emoji' }}>
      <SEO
        title="WhatsApp Business API Services – Vishwa DigiTech"
        description="Integrate WhatsApp Business API for your business with Vishwa DigiTech. Send automated messages, chatbots, bulk notifications & customer support via WhatsApp. Boost conversions & customer engagement."
        keywords="whatsapp business api india, whatsapp api integration sangli, whatsapp chatbot india, bulk whatsapp messaging, whatsapp marketing india, whatsapp api provider maharashtra"
        canonicalPath="/services/whatsapp-api"
      />
      
      {/* Custom Animations & Styles */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(1deg); }
        }
        @keyframes message-pop {
          0% { opacity: 0; transform: scale(0.9) translateY(10px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-float-delayed { animation: float 6s ease-in-out 3s infinite; }
        .animate-msg-1 { animation: message-pop 0.5s ease-out 0.5s forwards; opacity: 0; }
        .animate-msg-2 { animation: message-pop 0.5s ease-out 1.5s forwards; opacity: 0; }
        .animate-msg-3 { animation: message-pop 0.5s ease-out 2.5s forwards; opacity: 0; }
        
        .glass-card {
          background: rgba(255, 255, 255, 0.02);
          backdrop-filter: blur(15px);
          border: 1px solid rgba(255, 255, 255, 0.05);
          transition: all 0.4s ease;
        }
        .glass-card:hover {
          background: rgba(37, 211, 102, 0.05);
          border-color: rgba(37, 211, 102, 0.3);
          transform: translateY(-5px);
          box-shadow: 0 10px 40px -10px rgba(37, 211, 102, 0.15);
        }
        
        .text-gradient {
          background: linear-gradient(135deg, #ffffff 0%, #25D366 50%, #128C7E 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      `}</style>

      {/* Background Effects */}
      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden flex justify-center items-center">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-[#25D366]/15 rounded-full blur-[150px] animate-float" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[600px] h-[600px] bg-[#128C7E]/15 rounded-full blur-[150px] animate-float-delayed" />
      </div>

      <div className="relative z-10">
        
        {/* HERO SECTION */}
        <section className="pt-32 pb-24 px-4 max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            {/* Text Content */}
            <div className={`lg:w-1/2 transition-all duration-1000 transform ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'}`}>
              <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#25D366]/10 border border-[#25D366]/30 shadow-sm backdrop-blur-md mb-8">
                <BadgeCheck className="w-5 h-5 text-[#25D366]" />
                <span className="text-sm font-bold tracking-wide text-[#25D366] uppercase">WhatsApp Business API</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-black tracking-tight mb-8 leading-[1.1] text-white">
                Conversations that <br />
                <span className="text-gradient">Drive Revenue.</span>
              </h1>
              
              <p className="text-lg md:text-xl text-gray-400 font-light mb-10 leading-relaxed max-w-xl">
                Automate sales, support, and marketing on the app your customers already use. Achieve 98% open rates and scale your business with official WhatsApp Business API.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                <button className="px-8 py-4 bg-[#25D366] text-[#050810] rounded-xl font-bold text-lg hover:bg-[#20bd5a] transition-all shadow-[0_0_30px_rgba(37,211,102,0.4)] flex items-center justify-center gap-3 w-full sm:w-auto group hover:scale-105">
                  Get WhatsApp API <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <button className="px-8 py-4 bg-white/5 border border-white/10 text-white rounded-xl font-medium text-lg hover:bg-white/10 transition-colors flex items-center justify-center gap-3 w-full sm:w-auto">
                  <MessageSquare className="w-5 h-5 text-gray-400" /> Book Demo
                </button>
              </div>

              <div className="mt-10 flex items-center gap-6 text-sm text-gray-500 font-medium">
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#25D366]" /> Official Partner</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#25D366]" /> Green Tick Verification</div>
              </div>
            </div>

            {/* Mockup UI Graphic */}
            <div className={`lg:w-1/2 w-full relative transition-all duration-1000 delay-300 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
              <div className="relative mx-auto w-full max-w-sm">
                <div className="absolute inset-0 bg-gradient-to-r from-[#25D366]/20 to-[#128C7E]/20 blur-[100px] rounded-full"></div>
                <div className="bg-[#0A0F1F] border border-white/10 rounded-[2.5rem] shadow-2xl relative overflow-hidden h-[600px] flex flex-col">
                  {/* Phone Header */}
                  <div className="bg-[#128C7E] px-6 py-4 flex items-center gap-4">
                    <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                      <span className="text-[#128C7E] font-bold text-lg">VD</span>
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-lg leading-tight flex items-center gap-1">
                        Vishwa Digitech <BadgeCheck className="w-4 h-4 text-[#25D366] fill-white" />
                      </h3>
                      <p className="text-white/80 text-xs">Official Business Account</p>
                    </div>
                  </div>
                  {/* Chat Area */}
                  <div className="flex-1 bg-[#050810] p-4 flex flex-col gap-4 relative overflow-hidden" style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)', backgroundSize: '10px 10px' }}>
                    
                    <div className="bg-white/10 backdrop-blur-md self-center text-xs text-gray-400 px-3 py-1 rounded-full">Today</div>
                    
                    <div className="bg-white/5 border border-white/10 p-3 rounded-2xl rounded-tl-none self-start max-w-[85%] animate-msg-1">
                      <p className="text-sm text-gray-200">Hi! Welcome to Vishwa Digitech. How can we help you scale your business today? 🚀</p>
                    </div>
                    
                    <div className="bg-[#128C7E] p-3 rounded-2xl rounded-tr-none self-end max-w-[85%] animate-msg-2 shadow-lg">
                      <p className="text-sm text-white">I want to automate my customer support and marketing.</p>
                    </div>

                    <div className="bg-white/5 border border-white/10 p-3 rounded-2xl rounded-tl-none self-start max-w-[85%] animate-msg-3">
                      <p className="text-sm text-gray-200 mb-2">Great! We can set up a WhatsApp Chatbot and Broadcast Campaigns for you. Choose an option below:</p>
                      <div className="flex flex-col gap-2 mt-2 border-t border-white/10 pt-2">
                        <button className="text-[#25D366] text-sm font-semibold py-1">View Pricing</button>
                        <button className="text-[#25D366] text-sm font-semibold py-1 border-t border-white/10">Book a Demo</button>
                      </div>
                    </div>

                  </div>
                  {/* Phone Footer */}
                  <div className="bg-[#0A0F1F] p-4 border-t border-white/10 flex items-center gap-3">
                    <div className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-gray-500 text-sm">Type a message...</div>
                    <div className="w-10 h-10 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg cursor-pointer">
                      <Send className="w-4 h-4 text-[#050810] ml-1" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* METRICS SECTION */}
        <section className="py-16 border-y border-white/5 bg-white/[0.01]">
          <div className="max-w-7xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-8 divide-x divide-white/5">
            {[
              { stat: "98%", label: "Average Open Rate", color: "text-[#25D366]" },
              { stat: "45%", label: "Conversion Rate", color: "text-white" },
              { stat: "3x", label: "Faster Support", color: "text-[#25D366]" },
              { stat: "24/7", label: "Automated Sales", color: "text-white" }
            ].map((item, idx) => (
              <div key={idx} className="text-center px-4">
                <h3 className={`text-4xl md:text-5xl font-black mb-2 ${item.color}`}>{item.stat}</h3>
                <p className="text-gray-400 font-medium text-sm uppercase tracking-wider">{item.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* SERVICES GRID */}
        <section className="py-24 px-4 max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Powerful API Features</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#25D366] to-transparent mx-auto mb-6"></div>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto font-light">Unlock enterprise-grade features to engage, support, and sell at scale directly on WhatsApp.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Megaphone, title: "Broadcast Marketing", desc: "Send personalized promotional offers, alerts, and newsletters to thousands of opted-in customers instantly without getting blocked." },
              { icon: Bot, title: "AI Chatbot Automation", desc: "Automate FAQs, lead generation, and customer onboarding 24/7. Let intelligent bots handle 80% of routine inquiries." },
              { icon: ShieldCheck, title: "Green Tick Verification", desc: "Build immediate trust. We help you secure the Official WhatsApp Business API Green Tick badge for your brand profile." },
              { icon: Users, title: "Multi-Agent Inbox", desc: "Connect your entire support and sales team to a single WhatsApp number. Route chats efficiently and monitor performance." },
              { icon: Database, title: "CRM & ERP Integration", desc: "Seamlessly integrate WhatsApp with Shopify, WooCommerce, HubSpot, Zoho, and your custom backend systems." },
              { icon: BarChart, title: "Actionable Analytics", desc: "Track message delivery, open rates, read receipts, and campaign ROI with detailed, real-time analytics dashboards." }
            ].map((feature, idx) => (
              <div key={idx} className="glass-card p-8 rounded-3xl relative overflow-hidden group">
                <div className="absolute -right-10 -top-10 w-32 h-32 bg-[#25D366]/10 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="w-14 h-14 bg-[#25D366]/10 text-[#25D366] rounded-2xl flex items-center justify-center mb-6 border border-[#25D366]/20 group-hover:bg-[#25D366] group-hover:text-[#050810] transition-all duration-500 shadow-lg relative z-10">
                  <feature.icon className="w-7 h-7" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3 relative z-10">{feature.title}</h3>
                <p className="text-gray-400 leading-relaxed relative z-10">{feature.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* COMPARISON / WHY WHATSAPP SECTION */}
        <section className="py-24 px-4 max-w-7xl mx-auto">
          <div className="bg-[#0A0F1F] border border-white/10 rounded-[3rem] p-8 md:p-16 relative overflow-hidden shadow-2xl">
            <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#128C7E]/20 to-transparent pointer-events-none"></div>
            
            <div className="flex flex-col lg:flex-row items-center gap-16 relative z-10">
              <div className="lg:w-1/2">
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">Stop ignoring where your <br/><span className="text-[#25D366]">customers actually are.</span></h2>
                <p className="text-lg text-gray-400 mb-8 leading-relaxed">Traditional SMS is ignored, and Emails go to spam. WhatsApp is personal, immediate, and boasts unmatched engagement rates.</p>
                
                <div className="space-y-6">
                  <div className="flex items-center gap-4 bg-white/5 p-4 rounded-2xl border border-white/5">
                    <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center shrink-0">
                      <span className="text-slate-400 font-bold">SMS</span>
                    </div>
                    <div>
                      <h4 className="text-white font-bold">10-15% Open Rate</h4>
                      <p className="text-sm text-gray-500">Text only, prone to spam filters, low trust.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 bg-[#25D366]/10 p-4 rounded-2xl border border-[#25D366]/30 shadow-[0_0_15px_rgba(37,211,102,0.1)]">
                    <div className="w-12 h-12 rounded-full bg-[#25D366] flex items-center justify-center shrink-0 shadow-lg">
                      <MessageSquare className="w-6 h-6 text-[#050810] fill-current" />
                    </div>
                    <div>
                      <h4 className="text-[#25D366] font-bold text-lg">98% Open Rate</h4>
                      <p className="text-sm text-gray-300">Rich media, interactive buttons, trusted green tick.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:w-1/2 w-full grid grid-cols-2 gap-4">
                <div className="bg-[#050810] p-6 rounded-3xl border border-white/10 text-center flex flex-col items-center justify-center gap-3">
                  <div className="w-12 h-12 bg-blue-500/20 text-blue-400 rounded-full flex items-center justify-center"><Smartphone className="w-6 h-6" /></div>
                  <h4 className="font-bold text-white">E-Commerce</h4>
                  <p className="text-xs text-gray-500">Order updates & Cart Recovery</p>
                </div>
                <div className="bg-[#050810] p-6 rounded-3xl border border-white/10 text-center flex flex-col items-center justify-center gap-3 mt-8">
                  <div className="w-12 h-12 bg-purple-500/20 text-purple-400 rounded-full flex items-center justify-center"><Users className="w-6 h-6" /></div>
                  <h4 className="font-bold text-white">Real Estate</h4>
                  <p className="text-xs text-gray-500">Property Images & Lead Gen</p>
                </div>
                <div className="bg-[#050810] p-6 rounded-3xl border border-white/10 text-center flex flex-col items-center justify-center gap-3 -mt-8">
                  <div className="w-12 h-12 bg-orange-500/20 text-orange-400 rounded-full flex items-center justify-center"><Zap className="w-6 h-6" /></div>
                  <h4 className="font-bold text-white">Education</h4>
                  <p className="text-xs text-gray-500">Course Updates & Queries</p>
                </div>
                <div className="bg-[#050810] p-6 rounded-3xl border border-white/10 text-center flex flex-col items-center justify-center gap-3">
                  <div className="w-12 h-12 bg-[#25D366]/20 text-[#25D366] rounded-full flex items-center justify-center"><BadgeCheck className="w-6 h-6" /></div>
                  <h4 className="font-bold text-white">Any Business</h4>
                  <p className="text-xs text-gray-500">Customer Support & FAQs</p>
                </div>
              </div>
            </div>
          </div>
        </section>

      

      </div>
    </div>
  );
};

export default Whatsappapi;
